import {Three as THREE} from "@cm/material-nodes/three"
import potpack, {PotpackBox} from "potpack"

// An island needs at least one texel center inside it, otherwise the mask stays empty and it renders fully lit
const MIN_ISLAND_EXTENT_TEXELS = 2
// Share of the atlas that minimum extents and padding may take from everything else before meshes fall back to compact packing
const ISLAND_OVERHEAD_BUDGET = 0.1
const MAX_ISLAND_OVERLAPPING_RATIO = 0.1
// Below this share of its bounding box, an island's triangles are slivers that cover no texels, while its box scaled to
// texel density would still take up a large part of the atlas
const MIN_ISLAND_UV_FILL = 0.01
// Meshes get atlas area in proportion to their surface area raised to this power rather than to the area itself, which
// moves texels from large walls and floors, whose shadows are mostly soft, to small objects with fine shadow detail
const TEXEL_AREA_EXPONENT = 0.75
const PACKING_EFFICIENCY = 0.85
const MAX_PACKING_ATTEMPTS = 4
const OVERLAP_RASTER_RESOLUTION = 64

// Each smoothing pass grows the mask by one texel, so islands closer than twice the padding start blending into each other
export const islandPaddingTexels = (smoothIterations: number) => Math.max(2, Math.floor(smoothIterations / 2) + 1)

export type UVIslands = {
    count: number
    vertexIsland: Int32Array
    bounds: Float64Array
    size: Float64Array
    signedUVArea: Float64Array
    overlappingRatio: Float64Array
    uvFill: Float64Array
}

export type UVGeometryAnalysis = {
    surfaceArea: number
    uvArea: number
    acceptedIslandCount: number
    islands: UVIslands
    compactLayout?: {width: number; height: number; offsets: Float64Array}
}

export const isIslandAccepted = (islands: UVIslands, island: number) =>
    islands.overlappingRatio[island] <= MAX_ISLAND_OVERLAPPING_RATIO && islands.uvFill[island] >= MIN_ISLAND_UV_FILL

type CachedUVGeometryAnalysis = {
    position: THREE.BufferAttribute | THREE.InterleavedBufferAttribute
    sourceUV: THREE.BufferAttribute | THREE.InterleavedBufferAttribute
    index: THREE.BufferAttribute
    positionVersion: number
    uvVersion: number
    indexVersion: number
    uv: THREE.BufferAttribute
    analysis: UVGeometryAnalysis
}

const analysisCache = new WeakMap<THREE.BufferGeometry, CachedUVGeometryAnalysis>()

const attributeVersion = (attribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute) =>
    attribute instanceof THREE.InterleavedBufferAttribute ? attribute.data.version : attribute.version

export const getUVGeometryAnalysis = (geometry: THREE.BufferGeometry) => {
    const position = geometry.getAttribute("position")
    const sourceUV = geometry.getAttribute("uv")
    const index = geometry.getIndex()
    if (!index) throw new Error("Index is missing")

    const cached = analysisCache.get(geometry)
    if (
        cached &&
        cached.position === position &&
        cached.sourceUV === sourceUV &&
        cached.index === index &&
        cached.positionVersion === attributeVersion(position) &&
        cached.uvVersion === attributeVersion(sourceUV) &&
        cached.indexVersion === attributeVersion(index)
    )
        return {uv: cached.uv, analysis: cached.analysis}

    const uv = sourceUV instanceof THREE.InterleavedBufferAttribute ? sourceUV.clone() : sourceUV
    const analysis = analyzeUVGeometry(position, uv, index)
    analysisCache.set(geometry, {
        position,
        sourceUV,
        index,
        positionVersion: attributeVersion(position),
        uvVersion: attributeVersion(sourceUV),
        indexVersion: attributeVersion(index),
        uv,
        analysis,
    })
    return {uv, analysis}
}

const toPositionArray = (positions: THREE.BufferAttribute | THREE.InterleavedBufferAttribute) => {
    if (positions instanceof THREE.BufferAttribute && positions.itemSize === 3 && !positions.normalized) return positions.array
    const xyz = new Float32Array(positions.count * 3)
    for (let i = 0; i < positions.count; i++) {
        xyz[i * 3] = positions.getX(i)
        xyz[i * 3 + 1] = positions.getY(i)
        xyz[i * 3 + 2] = positions.getZ(i)
    }
    return xyz
}

const float32Bits = (array: THREE.TypedArray) =>
    array instanceof Float32Array ? new Uint32Array(array.buffer, array.byteOffset, array.length) : new Uint32Array(Float32Array.from(array).buffer)

export const analyzeUVGeometry = (
    positions: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
    uv: THREE.BufferAttribute,
    index: THREE.BufferAttribute,
): UVGeometryAnalysis => {
    if (uv.itemSize !== 2) throw new Error("UV item size must be 2")
    const indices = index.array
    if (indices.length === 0) throw new Error("Index is empty")
    if (indices.length % 3 !== 0) throw new Error("Index is not a multiple of 3")

    const xyz = toPositionArray(positions)
    const {count, vertexIsland} = findUVIslands(xyz, uv.array, indices, uv.count)
    return measureUVIslands(xyz, uv.array, indices, count, vertexIsland)
}

const findUVIslands = (xyz: THREE.TypedArray, uvs: THREE.TypedArray, indices: THREE.TypedArray, vertexCount: number) => {
    const parent = new Int32Array(vertexCount)
    for (let i = 0; i < vertexCount; i++) parent[i] = i
    const find = (i: number) => {
        while (parent[i] !== i) {
            parent[i] = parent[parent[i]]
            i = parent[i]
        }
        return i
    }
    const union = (a: number, b: number) => {
        const rootA = find(a)
        const rootB = find(b)
        if (rootA !== rootB) parent[rootB] = rootA
    }

    // Hard edges split vertices that keep position and UV; mirrored halves sharing a UV layout differ in position and stay apart
    const uvBits = float32Bits(uvs)
    const positionBits = float32Bits(xyz)
    const tableMask = (1 << Math.ceil(Math.log2(vertexCount * 2 + 1))) - 1
    const table = new Int32Array(tableMask + 1).fill(-1)
    for (let vertex = 0; vertex < vertexCount; vertex++) {
        const u = uvBits[vertex * 2]
        const v = uvBits[vertex * 2 + 1]
        const x = positionBits[vertex * 3]
        const y = positionBits[vertex * 3 + 1]
        const z = positionBits[vertex * 3 + 2]
        let hash = Math.imul(u ^ Math.imul(v, 0x9e3779b1), 0x85ebca6b)
        hash = Math.imul(hash ^ x ^ (hash >>> 15), 0xc2b2ae35)
        hash = Math.imul(hash ^ y ^ (hash >>> 13), 0x27d4eb2f)
        hash = Math.imul(hash ^ z ^ (hash >>> 16), 0x165667b1)
        let slot = (hash ^ (hash >>> 15)) & tableMask
        while (table[slot] >= 0) {
            const other = table[slot]
            if (
                uvBits[other * 2] === u &&
                uvBits[other * 2 + 1] === v &&
                positionBits[other * 3] === x &&
                positionBits[other * 3 + 1] === y &&
                positionBits[other * 3 + 2] === z
            ) {
                union(other, vertex)
                break
            }
            slot = (slot + 1) & tableMask
        }
        if (table[slot] < 0) table[slot] = vertex
    }

    for (let i = 0; i < indices.length; i += 3) {
        union(indices[i], indices[i + 1])
        union(indices[i], indices[i + 2])
    }

    const islandOfRoot = new Int32Array(vertexCount).fill(-1)
    const vertexIsland = new Int32Array(vertexCount).fill(-1)
    let count = 0
    for (let i = 0; i < indices.length; i++) {
        const vertex = indices[i]
        if (vertexIsland[vertex] >= 0) continue
        const root = find(vertex)
        if (islandOfRoot[root] < 0) islandOfRoot[root] = count++
        vertexIsland[vertex] = islandOfRoot[root]
    }

    return {count, vertexIsland}
}

const measureUVIslands = (
    xyz: THREE.TypedArray,
    uvs: THREE.TypedArray,
    indices: THREE.TypedArray,
    count: number,
    vertexIsland: Int32Array,
): UVGeometryAnalysis => {
    const bounds = new Float64Array(count * 4)
    for (let i = 0; i < count; i++) {
        bounds[i * 4] = Infinity
        bounds[i * 4 + 1] = Infinity
        bounds[i * 4 + 2] = -Infinity
        bounds[i * 4 + 3] = -Infinity
    }
    for (let vertex = 0; vertex < vertexIsland.length; vertex++) {
        const island = vertexIsland[vertex]
        if (island < 0) continue
        const u = uvs[vertex * 2]
        const v = uvs[vertex * 2 + 1]
        if (u < bounds[island * 4]) bounds[island * 4] = u
        if (v < bounds[island * 4 + 1]) bounds[island * 4 + 1] = v
        if (u > bounds[island * 4 + 2]) bounds[island * 4 + 2] = u
        if (v > bounds[island * 4 + 3]) bounds[island * 4 + 3] = v
    }

    const islandUVArea = new Float64Array(count)
    const islandSignedUVArea = new Float64Array(count)
    const islandSurfaceArea = new Float64Array(count)
    let uvArea = 0
    let surfaceArea = 0

    for (let i = 0; i < indices.length; i += 3) {
        const a = indices[i]
        const b = indices[i + 1]
        const c = indices[i + 2]
        const island = vertexIsland[a]

        const au = uvs[a * 2]
        const av = uvs[a * 2 + 1]
        const det = (uvs[b * 2] - au) * (uvs[c * 2 + 1] - av) - (uvs[c * 2] - au) * (uvs[b * 2 + 1] - av)
        if (isFinite(det)) {
            islandSignedUVArea[island] += det / 2
            islandUVArea[island] += Math.abs(det) / 2
            uvArea += Math.abs(det) / 2
        }

        const ax = xyz[a * 3]
        const ay = xyz[a * 3 + 1]
        const az = xyz[a * 3 + 2]
        const e1x = xyz[b * 3] - ax
        const e1y = xyz[b * 3 + 1] - ay
        const e1z = xyz[b * 3 + 2] - az
        const e2x = xyz[c * 3] - ax
        const e2y = xyz[c * 3 + 1] - ay
        const e2z = xyz[c * 3 + 2] - az
        const cx = e1y * e2z - e1z * e2y
        const cy = e1z * e2x - e1x * e2z
        const cz = e1x * e2y - e1y * e2x
        const faceArea = Math.sqrt(cx * cx + cy * cy + cz * cz) / 2
        if (isFinite(faceArea)) {
            islandSurfaceArea[island] += faceArea
            surfaceArea += faceArea
        }
    }

    const uvFill = new Float64Array(count)
    for (let island = 0; island < count; island++) {
        const boundsArea = (bounds[island * 4 + 2] - bounds[island * 4]) * (bounds[island * 4 + 3] - bounds[island * 4 + 1])
        uvFill[island] = boundsArea > 0 ? islandUVArea[island] / boundsArea : 0
    }

    const meshScale = Math.sqrt(surfaceArea / uvArea)
    const densityScale = surfaceArea > 0 ? Math.pow(surfaceArea, (TEXEL_AREA_EXPONENT - 1) / 2) : 1
    const size = new Float64Array(count * 2)
    for (let island = 0; island < count; island++) {
        const islandScale = Math.sqrt(islandSurfaceArea[island] / islandUVArea[island])
        const scale = (islandScale > 0 && isFinite(islandScale) ? islandScale : meshScale) * densityScale
        size[island * 2] = (bounds[island * 4 + 2] - bounds[island * 4]) * scale
        size[island * 2 + 1] = (bounds[island * 4 + 3] - bounds[island * 4 + 1]) * scale
    }

    const islands = {
        count,
        vertexIsland,
        bounds,
        size,
        signedUVArea: islandSignedUVArea,
        overlappingRatio: islandOverlappingRatios(uvs, indices, vertexIsland, count, bounds),
        uvFill,
    }

    let acceptedIslandCount = 0
    for (let island = 0; island < count; island++) if (isIslandAccepted(islands, island)) acceptedIslandCount++

    return {surfaceArea, uvArea, acceptedIslandCount, islands}
}

const islandOverlappingRatios = (uvs: THREE.TypedArray, indices: THREE.TypedArray, vertexIsland: Int32Array, count: number, bounds: Float64Array) => {
    const triangleCount = indices.length / 3
    const offsets = new Int32Array(count + 1)
    for (let triangle = 0; triangle < triangleCount; triangle++) offsets[vertexIsland[indices[triangle * 3]] + 1]++
    for (let island = 0; island < count; island++) offsets[island + 1] += offsets[island]
    const cursor = offsets.slice(0, count)
    const triangles = new Int32Array(triangleCount)
    for (let triangle = 0; triangle < triangleCount; triangle++) triangles[cursor[vertexIsland[indices[triangle * 3]]]++] = triangle

    let minU = Infinity
    let minV = Infinity
    let maxU = -Infinity
    let maxV = -Infinity
    for (let island = 0; island < count; island++) {
        minU = Math.min(minU, bounds[island * 4])
        minV = Math.min(minV, bounds[island * 4 + 1])
        maxU = Math.max(maxU, bounds[island * 4 + 2])
        maxV = Math.max(maxV, bounds[island * 4 + 3])
    }

    const ratios = new Float64Array(count)
    const uSize = maxU - minU
    const vSize = maxV - minV
    const uvToRaster = OVERLAP_RASTER_RESOLUTION / Math.max(uSize, vSize)
    const resX = Math.ceil(uSize * uvToRaster)
    const resY = Math.ceil(vSize * uvToRaster)
    if (!(resX > 0 && resY > 0)) return ratios

    // Islands overlapping each other are packed apart, so a pixel only counts as overlap when its own island covers it twice
    const owner = new Int32Array(resX * resY).fill(-1)
    const coverage = new Uint8Array(resX * resY)

    for (let island = 0; island < count; island++) {
        let totalPixels = 0
        let totalOverlappingPixels = 0

        for (let i = offsets[island]; i < offsets[island + 1]; i++) {
            const triangle = triangles[i]
            const a = indices[triangle * 3]
            const b = indices[triangle * 3 + 1]
            const c = indices[triangle * 3 + 2]
            const x0 = (uvs[a * 2] - minU) * uvToRaster
            const y0 = (uvs[a * 2 + 1] - minV) * uvToRaster
            const x1 = (uvs[b * 2] - minU) * uvToRaster
            const y1 = (uvs[b * 2 + 1] - minV) * uvToRaster
            const x2 = (uvs[c * 2] - minU) * uvToRaster
            const y2 = (uvs[c * 2 + 1] - minV) * uvToRaster

            const area = (x1 - x0) * (y2 - y0) - (y1 - y0) * (x2 - x0)
            if (area === 0 || !isFinite(area)) continue
            const sign = Math.sign(area)

            const xMin = Math.max(0, Math.floor(Math.min(x0, x1, x2)))
            const xMax = Math.min(resX - 1, Math.ceil(Math.max(x0, x1, x2)))
            const yMin = Math.max(0, Math.floor(Math.min(y0, y1, y2)))
            const yMax = Math.min(resY - 1, Math.ceil(Math.max(y0, y1, y2)))

            for (let y = yMin; y <= yMax; y++) {
                const py = y + 0.5
                for (let x = xMin; x <= xMax; x++) {
                    const px = x + 0.5
                    const w0 = ((x2 - x1) * (py - y1) - (y2 - y1) * (px - x1)) * sign
                    const w1 = ((x0 - x2) * (py - y2) - (y0 - y2) * (px - x2)) * sign
                    const w2 = ((x1 - x0) * (py - y0) - (y1 - y0) * (px - x0)) * sign
                    if (w0 <= 0 || w1 <= 0 || w2 <= 0) continue

                    const pixel = y * resX + x
                    if (owner[pixel] !== island) {
                        owner[pixel] = island
                        coverage[pixel] = 1
                        totalPixels++
                    } else if (coverage[pixel] === 1) {
                        coverage[pixel] = 2
                        totalOverlappingPixels++
                    }
                }
            }
        }

        ratios[island] = totalPixels > 0 ? totalOverlappingPixels / totalPixels : 0
    }

    return ratios
}

const getCompactLayout = (analysis: UVGeometryAnalysis) => {
    if (analysis.compactLayout) return analysis.compactLayout

    const {islands} = analysis
    const boxes: (PotpackBox & {island: number})[] = []
    for (let island = 0; island < islands.count; island++) {
        if (isIslandAccepted(islands, island)) boxes.push({w: islands.size[island * 2], h: islands.size[island * 2 + 1], island})
    }
    const {w, h} = potpack(boxes)

    const offsets = new Float64Array(islands.count * 2)
    for (const {island, x, y} of boxes) {
        if (x === undefined || y === undefined) throw new Error("x or y is undefined")
        offsets[island * 2] = x
        offsets[island * 2 + 1] = y
    }

    analysis.compactLayout = {width: w, height: h, offsets}
    return analysis.compactLayout
}

export type UVAtlasLayout = {
    width: number
    height: number
    placements: Float64Array[]
    compact: boolean[]
    litPoint: [number, number]
}

type AtlasBox = PotpackBox & {entry: number; island: number; width: number; height: number}

const LIT_BOX = -1
const COMPACT_BOX = -2

export const layoutUVAtlas = (analyses: UVGeometryAnalysis[], resolution: number, paddingTexels: number): UVAtlasLayout => {
    let rawArea = 0
    let hasRejectedIslands = false
    for (const {islands, acceptedIslandCount} of analyses) {
        for (let island = 0; island < islands.count; island++)
            if (isIslandAccepted(islands, island)) rawArea += islands.size[island * 2] * islands.size[island * 2 + 1]
        if (acceptedIslandCount < islands.count) hasRejectedIslands = true
    }

    const compact = analyses.map(() => false)
    let texelSize = rawArea > 0 ? Math.sqrt(rawArea / PACKING_EFFICIENCY) / resolution : 1 / resolution
    let padding = 0
    let boxes: AtlasBox[] = []
    let dimensions = {w: 0, h: 0}

    for (let attempt = 0; attempt < MAX_PACKING_ATTEMPTS; attempt++) {
        const minExtent = MIN_ISLAND_EXTENT_TEXELS * texelSize
        padding = paddingTexels * texelSize

        const islandBox = (width: number, height: number) => {
            const w = Math.max(width, minExtent)
            const h = Math.max(height, minExtent)
            return {w, h, overhead: (w + padding * 2) * (h + padding * 2) - width * height}
        }

        const savings = analyses.map(({islands}, entry) => {
            let overhead = 0
            for (let island = 0; island < islands.count; island++) {
                if (isIslandAccepted(islands, island)) overhead += islandBox(islands.size[island * 2], islands.size[island * 2 + 1]).overhead
            }
            const {width, height} = getCompactLayout(analyses[entry])
            const compactOverhead = (width + padding * 2) * (height + padding * 2) - width * height
            return {entry, overhead, saving: overhead - compactOverhead}
        })

        let totalOverhead = savings.reduce((sum, {overhead}) => sum + overhead, 0)
        const budget = ISLAND_OVERHEAD_BUDGET * (resolution * texelSize) ** 2
        compact.fill(false)
        for (const {entry, saving} of savings.sort((a, b) => b.saving - a.saving)) {
            if (totalOverhead <= budget || saving <= 0) break
            compact[entry] = true
            totalOverhead -= saving
        }

        boxes = []
        analyses.forEach((analysis, entry) => {
            const {islands} = analysis
            if (compact[entry]) {
                const {width, height} = getCompactLayout(analysis)
                boxes.push({w: width + padding * 2, h: height + padding * 2, entry, island: COMPACT_BOX, width, height})
                return
            }
            for (let island = 0; island < islands.count; island++) {
                if (!isIslandAccepted(islands, island)) continue
                const {w, h} = islandBox(islands.size[island * 2], islands.size[island * 2 + 1])
                boxes.push({w: w + padding * 2, h: h + padding * 2, entry, island, width: w, height: h})
            }
        })
        if (hasRejectedIslands)
            boxes.push({w: minExtent + padding * 2, h: minExtent + padding * 2, entry: -1, island: LIT_BOX, width: minExtent, height: minExtent})

        dimensions = potpack(boxes)

        const packedTexelSize = Math.sqrt(dimensions.w * dimensions.h) / resolution
        if (packedTexelSize <= texelSize * 1.05) break
        texelSize = packedTexelSize
    }

    const placements = analyses.map(({islands}) => new Float64Array(islands.count * 4))
    let litPoint: [number, number] = [0, 0]
    for (const box of boxes) {
        if (box.x === undefined || box.y === undefined) throw new Error("x or y is undefined")
        const x = box.x + padding
        const y = box.y + padding

        if (box.island === LIT_BOX) {
            litPoint = [(x + box.width / 2) / dimensions.w, (y + box.height / 2) / dimensions.h]
        } else if (box.island === COMPACT_BOX) {
            const {islands} = analyses[box.entry]
            const {offsets} = getCompactLayout(analyses[box.entry])
            const placement = placements[box.entry]
            for (let island = 0; island < islands.count; island++) {
                placement[island * 4] = x + offsets[island * 2]
                placement[island * 4 + 1] = y + offsets[island * 2 + 1]
                placement[island * 4 + 2] = islands.size[island * 2]
                placement[island * 4 + 3] = islands.size[island * 2 + 1]
            }
        } else {
            const placement = placements[box.entry]
            placement[box.island * 4] = x
            placement[box.island * 4 + 1] = y
            placement[box.island * 4 + 2] = box.width
            placement[box.island * 4 + 3] = box.height
        }
    }

    return {width: dimensions.w, height: dimensions.h, placements, compact, litPoint}
}

export const writeAtlasUVs = (
    uv: THREE.BufferAttribute,
    analysis: UVGeometryAnalysis,
    side: THREE.Side,
    layout: UVAtlasLayout,
    entry: number,
    target: Float32Array,
) => {
    const {islands} = analysis
    const {vertexIsland, bounds, signedUVArea} = islands
    const placement = layout.placements[entry]
    const uvs = uv.array

    // The bake rasterizes with flipped V, so front faces need a negative UV winding
    const windingSign = side === THREE.FrontSide ? -1 : side === THREE.BackSide ? 1 : 0
    const mirrored = new Uint8Array(islands.count)
    for (let island = 0; island < islands.count; island++) mirrored[island] = signedUVArea[island] * windingSign < 0 ? 1 : 0

    const accepted = new Uint8Array(islands.count)
    for (let island = 0; island < islands.count; island++) accepted[island] = isIslandAccepted(islands, island) ? 1 : 0

    for (let vertex = 0; vertex < uv.count; vertex++) {
        const island = vertexIsland[vertex]
        if (island < 0 || !accepted[island]) {
            target[vertex * 2] = layout.litPoint[0]
            target[vertex * 2 + 1] = layout.litPoint[1]
            continue
        }

        const minU = bounds[island * 4]
        const minV = bounds[island * 4 + 1]
        const rawU = bounds[island * 4 + 2] - minU
        const rawV = bounds[island * 4 + 3] - minV
        const width = placement[island * 4 + 2]
        const height = placement[island * 4 + 3]

        let u = rawU > 0 ? ((uvs[vertex * 2] - minU) / rawU) * width : width / 2
        const v = rawV > 0 ? ((uvs[vertex * 2 + 1] - minV) / rawV) * height : height / 2
        if (mirrored[island]) u = width - u

        target[vertex * 2] = (placement[island * 4] + u) / layout.width
        target[vertex * 2 + 1] = (placement[island * 4 + 1] + v) / layout.height
    }
}
